import z from "zod";
import { Button, Group, Select, Stack, Textarea, TextInput } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { LinkBack } from "../../../components/Link";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { useTRPC } from "../../../trpc";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { assertAccountSelected, useUser } from "../../../hooks/user";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useNavigate } from "@tanstack/react-router";
import { useHandleStyleCreation } from "../../../hooks/styleActions.js";
import { LimitExceeded } from "../components/LimitExceeded.js";
import { ErrorAlert } from "../components/ErrorAlert.js";
import { DEFAULT_BASEMAP_KEY, TieredPriceProductId } from "@project/common";
import { useProductNames } from "../components/hooks/productLabels.js";

export const NewMap = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const user = useUser();
  const navigate = useNavigate();
  assertAccountSelected(user);

  const [errorMessage, setErrorMessage] = useState<string>();
  const { data: styles = [] } = useQuery(trpc.getStylesWithCurrentVersion.queryOptions());
  const baseMaps = useMemo(
    () => styles.filter(({ style }) => style.scope === "public"),
    [styles],
  );

  const {
    name: accountName,
  } = user.currentAccount;

  const validate = useMemo(() => zod4Resolver(z.object({
    displayName: z.string()
      .min(1, {
        message: intl.formatMessage({
          defaultMessage: "Map name is required",
          id: "error.mapNameRequired",
        }),
      })
      .max(255, {
        message: intl.formatMessage({
          defaultMessage: "Map name is too long",
          id: "error.mapNameTooLong",
        }),
      }),
    description: z.string().optional(),
    sourceStyleKey: z.string().min(1, {
      message: intl.formatMessage({
        defaultMessage: "Template is required",
        id: "error.templateRequired",
      }),
    }),
  })), [intl]);

  const options = useMemo(() => {
    return baseMaps.map(({ style }) => ({ value: style.key, label: style.displayName }));
  }, [baseMaps]);

  const { onSubmit, getInputProps, reset } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      displayName: "",
      description: "",
      sourceStyleKey: DEFAULT_BASEMAP_KEY,
    },
    validate,
  });

  const {
    createStyle,
    isPending,
  } = useHandleStyleCreation(setErrorMessage, reset);

  const handleCreate = useCallback(({ sourceStyleKey, ...styleData }: {
    displayName: string;
    description: string;
    sourceStyleKey: string;
  }) => {
    const source = baseMaps.find(({ style }) => style.key === sourceStyleKey);
    if (!source) {
      setErrorMessage(intl.formatMessage({
        defaultMessage: "Template is required",
        id: "error.templateRequired",
      }));
      return;
    }
    createStyle({ ...styleData, sourceStyleId: source.style.id });
  }, [baseMaps, createStyle, intl]);

  const handleCancel = useCallback(() => {
    reset();
    setErrorMessage(undefined);
    void navigate({ to: "/$accountName/maps",
      params: {
        accountName,
      },
    });
  }, [reset]);

  let errorMessageNode: ReactNode = null;
  const getName = useProductNames();

  if (errorMessage) {
    if (errorMessage.includes("Style limit exceeded")) {
      errorMessageNode = (
        <LimitExceeded
          limitOfWhatExceeded={getName(TieredPriceProductId.TieredCustomStyle)}
        />
      );
    }
    else {
      errorMessageNode = errorMessage;
    }
  }

  return (
    <Stack px="md" my="md">
      <SimpleBreadcrumbs
        parent={<LinkBack href="/app/maps"><FormattedMessage defaultMessage="Maps" id="navigation.maps" /></LinkBack>}
        current={intl.formatMessage({ defaultMessage: "New Map", id: "navigation.newMap" })}
        description={intl.formatMessage({ defaultMessage: "Create new map, define config and integrations.", id: "text.newMapDescription" })}
      />

      <Stack>
        {errorMessage && (
          <ErrorAlert>
            {errorMessageNode}
          </ErrorAlert>
        )}
        <form onSubmit={onSubmit(handleCreate)}>
          <Stack gap="md">
            <TextInput
              label={intl.formatMessage({ defaultMessage: "Map Name", id: "label.mapName" })}
              placeholder={intl.formatMessage({ defaultMessage: "My awesome map", id: "placeholder.mapDisplayName" })}
              required
              {...getInputProps("displayName")}
            />

            <Textarea
              label={intl.formatMessage({ defaultMessage: "Description", id: "label.description" })}
              placeholder={intl.formatMessage({ defaultMessage: "Enter map description...", id: "placeholder.mapDescription" })}
              {...getInputProps("description")}
            />

            <Select
              label={intl.formatMessage({ defaultMessage: "Base map", id: "label.baseMap" })}
              placeholder={intl.formatMessage({ defaultMessage: "Select a base map", id: "placeholder.selectBaseMap" })}
              data={options}
              required
              {...getInputProps("sourceStyleKey")}
            />

            <Group gap="md" justify="flex-end" mt="md">
              <Button variant="secondary" onClick={handleCancel} disabled={isPending}>
                <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
              </Button>
              <Button variant="tertiary" type="submit" loading={isPending}>
                <FormattedMessage defaultMessage="Create" id="button.create" />
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};
